<template>
  <div
    class="news-badge"
    @mouseenter=" isHovered = true;"
    @mouseleave=" isHovered = false;">
    <nuxt-link
      :to="localePath({
        ...(!initiative && category ? {
          name: 'news-category-slug', params: { category: category.slug, slug },
        } : {}),
        ...(initiative ? {
          name: 'initiatives-slug', params: { slug },
        } : {}),
      })">

      <span class="news-badge__subtitle">{{ subtitle }}</span>

      <veneta-icon
        class="veneta-icon"
        color="white"
        :is-hovered="isHovered" />

      <span class="news-badge__title">{{ title }}</span>

    </nuxt-link>
  </div>
</template>

<script setup>
  const localePath = useLocalePath()
  const isHovered = ref(false)

  // Props
  defineProps({
    subtitle: {
      type: String,
      default: 'News'
    },
    category: Object,
    title: {
      type: String,
      required: true
    },
    slug: String,
    initiative: Boolean
  })
</script>

<style lang="scss" scoped>
.news-badge {
  width: 100%;
  position: relative;
  isolation: isolate;
  background-color: rgb(255, 255, 255, 0.2);
  border: 1px solid var(--color-white);
  border-radius: 8px;
  overflow: hidden;
  color: var(--color-white);
  padding: rem(24px);
  transition: color var(--timing-default) var(--easing-default);

  @include mq($until: tablet) {
    width: 100%;
    max-width: 280px;
  }

  // Keep the rasterized backdrop layer below Chromium's tile width. Scaling
  // the already-blurred pixels is visually lossless and prevents tile seams.
  &::after {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 80%;
    z-index: 0;
    pointer-events: none;
    backdrop-filter: blur(20px) saturate(180%);
    transform: scaleX(1.25);
    transform-origin: left center;
    will-change: backdrop-filter;
  }

  // Sliding background on hover (same effect as solid CTA)
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    z-index: 1;
    background: transparent;
    transition:
      left var(--timing-default) var(--easing-default),
      background-color var(--timing-default) var(--easing-default);
  }

  &:hover {
    color: var(--color-black);
    transition: color 0.4s var(--easing-default);

    &::before {
      left: 0;
      background: var(--color-white);
      transition:
        left 0.4s var(--easing-default),
        background-color 0.4s var(--easing-default);
    }

    .news-badge__subtitle {
      color: var(--color-red);
      transition: color 0.4s var(--easing-default);
    }

    :deep(.lottie-animation) {
      filter: none;
    }
  }

  a span {
    display: block;
  }

  a {
    position: relative;
    z-index: 2;
    display: grid;
    gap: rem(16px);
    grid-template-columns: minmax(0, auto) minmax(0, 24px);
  }

  &__subtitle {
    text-transform: uppercase;
    color: var(--color-white);
    transition: color var(--timing-default) var(--easing-default);
  }

  &__title {
    max-width: rem(280px);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    grid-column: 1 / span 2;
  }
}
</style>
